// src/components/CustomerFlow/CustomerFlow.js

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
// Importar estilos base de RequestFlow
import '../RequestFlow/RequestFlow.css';
// Importar estilos específicos de CustomerFlow
import './CustomerFlow.css';

// Importar todos los componentes de pasosGeoLocationValidationStep
import IdentityValidationStep from './Steps/IdentityValidationStep';
import PaymentProcessingStep from './Steps/PaymentProcessingStep';
import PaymentConfirmationStep from './Steps/PaymentConfirmationStep';
import ConfirmationStep from './Steps/ConfirmationStep';
import DocumentUploadStep from './Steps/DocumentUploadStep';
import PhoneValidationStep from './Steps/PhoneValidationStep';
import PreSignatureConfirmationStep from './Steps/PreSignatureConfirmationStep';
import GeoLocationValidationStep from './Steps/OnSiteValidationStep';

import FinalStep from './Steps/FinalStep';
import { trackMobileCustomerFlowStepEntered, trackMobileCustomerFlowStepCompleted, trackMobileCustomerFlowCompleted } from '../../analytics/events';
import { Stepper } from '../ui';
import RequestFlowErrorBoundary from '../RequestFlowErrorBoundary';

const stepComponentMap = {
  ValidaStep: IdentityValidationStep,
  PaymentStep: PaymentProcessingStep,
  PaymentConfirmationStep: PaymentConfirmationStep,
  ConfirmationStep: ConfirmationStep,
  DocumentUploadStep: DocumentUploadStep,
  PhoneValidationStep: PhoneValidationStep,
  PreSignatureConfirmationStep: PreSignatureConfirmationStep,
  GeoLocationValidationStep: GeoLocationValidationStep
};

const stepLabelMap = {
  ValidaStep: 'Identidad',
  PaymentStep: 'Medio de Pago',
  PaymentConfirmationStep: 'Pago y firma',
  ConfirmationStep: 'Resumen',
  DocumentUploadStep: 'Documentación',
  PhoneValidationStep: 'Validación de Teléfono',
  PreSignatureConfirmationStep: 'Confirmación y firma',
  GeoLocationValidationStep: 'Confirmación de ubicación'
};

const CustomerFlow = ({ 
  flowData, 
  stepStatuses, 
  timeRemaining, 
  onStepComplete,
  onStepBack,
  searchParams
}) => {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [stepLabels, setStepLabels] = useState([]);
  const [formData, setFormData] = useState({});
  const trackedStepsRef = useRef(new Set()); // Track which steps have been tracked for "entered" event

  const determineCurrentStepIndex = useCallback(() => {
    if (!flowData?.flow_config || Object.keys(stepStatuses).length === 0) {
      return 0;
    }

    const stepNames = flowData.flow_config;

    const inProgressIndex = stepNames.findIndex(
      stepName => stepStatuses[stepName] === 'in_progress'
    );
    if (inProgressIndex !== -1) {
      return inProgressIndex;
    }

    const allStepsCompleted = stepNames.every(stepName =>
      stepStatuses[stepName] === 'completed' || stepStatuses[stepName] === 'processed'
    );

    if (allStepsCompleted) {
      return stepNames.length;
    }

    const pendingIndex = stepNames.findIndex(
      stepName => stepStatuses[stepName] === 'pending'
    );
    if (pendingIndex !== -1) {
      return pendingIndex;
    }

    const lastProcessedIndex = stepNames.findIndex(stepName =>
      stepStatuses[stepName] === 'processed' || stepStatuses[stepName] === 'completed'
    );

    if (lastProcessedIndex >= 0 && lastProcessedIndex < stepNames.length - 1) {
      return lastProcessedIndex + 1;
    }

    return 0;
  }, [flowData, stepStatuses]);

  useEffect(() => {
    if (flowData?.flow_config && Object.keys(stepStatuses).length > 0) {
      const stepsArray = flowData.flow_config.map(stepName => stepComponentMap[stepName]).filter(Boolean);
      const labelsArray = flowData.flow_config.map(stepName => stepLabelMap[stepName] || stepName);
      
      // El FinalStep siempre está disponible para todos los flujos (pero no se muestra en el stepper)
      stepsArray.push(FinalStep);
      
      setSteps(stepsArray);
      setStepLabels(labelsArray); // Solo las etiquetas de los pasos del flujo
      
      // Determinar el paso actual basado en prioridades
      const targetStepIndex = determineCurrentStepIndex();
      setCurrentStepIndex(targetStepIndex);

      // Track step entered when flow initializes (only once per step)
      if (targetStepIndex < stepsArray.length && flowData?.flow_id) {
        const stepName = flowData.flow_config[targetStepIndex];
        if (stepName) {
          const stepKey = `${flowData.flow_id}-${stepName}`;
          if (!trackedStepsRef.current.has(stepKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepEntered({
              commerceId: branchId || 'mobile_flow', // Fallback, should be replaced with actual commerceId from API
              branchId: branchId || undefined,
              stepName: stepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepKey);
          }
        }
      }
    }
  }, [determineCurrentStepIndex, flowData, stepStatuses]);

  const handleNext = (data, stepStatus = null) => {
    setFormData((prevData) => {
      const updatedData = {
        ...prevData,
        ...data,
      };
      return updatedData;
    });

    // Todos los estados avanzan al siguiente paso
    if (stepStatus === 'completed' || stepStatus === 'processed' || stepStatus === 'in_progress') {
      // Track step completed only when actually completed (not just in_progress)
      // Use a ref to track which steps have been completed to avoid duplicates
      if (stepStatus === 'completed' || stepStatus === 'processed') {
        const stepName = flowData?.flow_config?.[currentStepIndex];
        if (stepName && flowData?.flow_id) {
          const stepCompletedKey = `${flowData.flow_id}-${stepName}-completed`;
          if (!trackedStepsRef.current.has(stepCompletedKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepCompleted({
              commerceId: branchId || 'mobile_flow', // Fallback, should be replaced with actual commerceId
              branchId: branchId || undefined,
              stepName: stepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepCompletedKey);
          }
        }
      }
      
      // Cualquier estado del paso - avanzar al siguiente
      onStepComplete(data);
      
      // Recalcular el paso actual después de completar
      setTimeout(() => {
        const newStepIndex = determineCurrentStepIndex();
        setCurrentStepIndex(newStepIndex);
        
        // Track step entered (only once per step)
        const newStepName = flowData?.flow_config?.[newStepIndex];
        if (newStepName && flowData?.flow_id) {
          const stepKey = `${flowData.flow_id}-${newStepName}`;
          if (!trackedStepsRef.current.has(stepKey)) {
            const branchId = localStorage.getItem('temp_tokenBranchId');
            trackMobileCustomerFlowStepEntered({
              commerceId: branchId || 'mobile_flow', // Fallback
              branchId: branchId || undefined,
              stepName: newStepName,
              flowId: flowData.flow_id
            });
            trackedStepsRef.current.add(stepKey);
          }
        }
        
        // Track flow completed if we're on FinalStep
        if (newStepIndex === steps.length - 1 && flowData?.flow_id) {
          const branchId = localStorage.getItem('temp_tokenBranchId');
          trackMobileCustomerFlowCompleted({
            commerceId: branchId || 'mobile_flow', // Fallback
            branchId: branchId || undefined,
            flowId: flowData.flow_id
          });
        }
      }, 100);
    }
  };

  const handleBack = () => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
      onStepBack();
    }
  };

  const renderStep = () => {
    const CurrentStepComponent = steps[currentStepIndex];

    if (!CurrentStepComponent) {
      return <div>Error al cargar el paso. Por favor, intenta nuevamente.</div>;
    }

    // Props específicas para el paso final
    if (CurrentStepComponent === FinalStep) {
      return (
        <FinalStep
          onComplete={handleNext}
        />
      );
    }

    return (
      <CurrentStepComponent
        flowData={flowData}
        stepStatuses={stepStatuses}
        timeRemaining={timeRemaining}
        onNext={handleNext}
        onBack={handleBack}
        formData={formData}
        currentStepIndex={currentStepIndex}
        totalSteps={steps.length}
        searchParams={searchParams}
      />
    );
  };

  const stepperSteps = stepLabels.map((label, index) => {
    const stepName = flowData?.flow_config?.[index];
    const isCompleted = stepStatuses[stepName] === 'completed' || stepStatuses[stepName] === 'processed';
    const isActive = index === currentStepIndex && !isCompleted;

    return {
      id: stepName || `step-${index}`,
      label,
      number: false,
      state: isCompleted ? 'completed' : isActive ? 'active' : undefined,
      icon: isCompleted ? <FontAwesomeIcon icon={faCheck} className="text-white" /> : <span>{index + 1}</span>
    };
  });

  const calculateProgress = () => {
    const flowSteps = flowData?.flow_config?.length || 0;
    if (flowSteps <= 1) return 0;

    if (currentStepIndex >= flowSteps) {
      return 100;
    }

    const completedSteps = Object.values(stepStatuses).filter((status) =>
      status === 'completed' || status === 'processed'
    ).length;

    return (completedSteps / (flowSteps - 1)) * 100;
  };

  if (!steps.length) {
    return (
      <div className="customer-flow rg-customer-flow">
        <div className="stepper-container rg-customer-flow__stepper rg-customer-flow__stepper--loading">
          <div className="rg-customer-flow__loading-copy">
            <Skeleton width={140} height={12} />
            <Skeleton width={220} height={18} />
          </div>
          <div className="rg-customer-flow__loading-steps">
            {Array.from({ length: 4 }).map((_, index) => (
              <div key={`step-skeleton-${index}`} className="rg-customer-flow__loading-step">
                <Skeleton circle width={34} height={34} />
                <Skeleton width={72} height={10} />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="customer-flow rg-customer-flow">
      {/* Stepper con barra de progreso - solo visible si hay más de 1 paso */}
      {stepLabels.length > 1 && (
        <div className="stepper-container rg-customer-flow__stepper">
          <Stepper
            steps={stepperSteps}
            className="stepper-wrapper rg-stepper--compact rg-stepper--customer-flow"
            ariaLabel="Estado del proceso"
          />
          <div className="progress-bar-wrapper rg-customer-flow__progress">
            <div
              className={`progress-bar ${calculateProgress() === 100 ? 'success' : ''}`}
              style={{ width: `${calculateProgress()}%` }}
            />
          </div>
        </div>
      )}

      {/* Contenedor del paso renderizado con elevación media */}
      <div className="step-content-container">
        <RequestFlowErrorBoundary key={currentStepIndex} operation="customer_flow.render">
          {renderStep()}
        </RequestFlowErrorBoundary>
      </div>
    </div>
  );
};

export default CustomerFlow;
